import React, { PropsWithChildren, createContext, useCallback, useContext, useMemo } from 'react'
import * as AuthSession from 'expo-auth-session'
import { observer } from 'mobx-react-lite'
import { ConvexProviderWithAuth, ConvexReactClient } from 'convex/react'

import { useStore } from '@/models'
import { refreshAccessToken } from '@/models/session/refreshToken'
import { useSessionStore } from '@/models/session/useSessionStore'
import { useRemoteConfig } from '../module/remote-config'
import { resolveConvexDeployment } from './resolveConvexDeployment'

// TODO: Change default to preview
const DEFAULT_CONVEX_URL = 'https://chatty-sandpiper-749.convex.cloud'
const CONVEX_URL = process.env.EXPO_PUBLIC_CONVEX_URL ?? DEFAULT_CONVEX_URL

export type ConvexDeployment = ReturnType<typeof resolveConvexDeployment>

const ConvexActiveDeploymentContext = createContext<ConvexDeployment>({
  convexUrl: CONVEX_URL,
  source: 'default'
})

/** Returns the active Convex deployment info, including the URL and where it was resolved from. */
export const useActiveConvexDeployment = () => useContext(ConvexActiveDeploymentContext)

function createConvexClient(convexUrl: string) {
  return new ConvexReactClient(convexUrl, {
    unsavedChangesWarning: false,
    onServerDisconnectError(message) {
      console.log('LOG: onServerDisconnectError > message:', message)
    }
  })
}

const useAuthConvex = () => {
  const store = useStore()
  const tokenConfig = useSessionStore((state) => state.tokenConfig)
  const isAuthenticated = !!tokenConfig?.idToken

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      // Get latest token from store (don't use closure over tokenConfig)
      const currentTokenConfig = useSessionStore.getState().tokenConfig

      if (!currentTokenConfig) {
        return null
      }

      if (forceRefreshToken) {
        // Convert to plain object for refreshAccessToken
        const newToken = await refreshAccessToken(
          JSON.parse(JSON.stringify(currentTokenConfig)) as AuthSession.TokenResponseConfig,
          store
        )
        return newToken?.idToken ?? null
      }

      // Return current idToken
      return currentTokenConfig.idToken ?? null
    },
    [store]
  )

  const authResponse = useMemo(
    () => ({
      isLoading: false,
      isAuthenticated,
      fetchAccessToken
    }),
    [isAuthenticated, fetchAccessToken]
  )

  return authResponse
}

export const ConvexProvider = observer(({ children }: PropsWithChildren) => {
  const store = useStore()
  const convexDeployment = store.environment.convexDeployment
  const convexUrlFeatureFlag = useRemoteConfig((config) => config.convex_config?.convex_deploy_url)

  const activeDeployment = useMemo(
    () =>
      resolveConvexDeployment({
        storeDeployment: convexDeployment,
        remoteConfigUrl: convexUrlFeatureFlag,
        defaultUrl: CONVEX_URL
      }),
    [convexDeployment, convexUrlFeatureFlag]
  )

  const convexClient = useMemo(() => createConvexClient(activeDeployment.convexUrl), [activeDeployment.convexUrl])

  return (
    <ConvexActiveDeploymentContext.Provider value={activeDeployment}>
      <ConvexProviderWithAuth client={convexClient} useAuth={useAuthConvex}>
        {children}
      </ConvexProviderWithAuth>
    </ConvexActiveDeploymentContext.Provider>
  )
})
